// Shim to integrate react-rails with modern Turbo (Hotwire)
// Based on https://github.com/reactjs/react-rails/blob/master/react_ujs/src/events/turbolinks.js
//
// react-rails only knows about turbolinks:load events, not modern Turbo events.
// This shim bridges that gap by listening to Turbo events and mounting React components.

let ReactRailsUJS = null

// Smart mount: only mount components that haven't been mounted yet.
// This prevents remounting existing components with stale props from data-react-props,
// which would cause loss of current state (e.g., MultiSelect selections).
function handleMount() {
  if (!ReactRailsUJS) return

  const components = document.querySelectorAll('[data-react-class]')
  components.forEach(element => {
    // Use ReactRailsUJS.findRoot to check if component is already mounted.
    // This is more reliable than checking childNodes since it uses the
    // library's internal root tracking.
    if (!ReactRailsUJS.findRoot(element)) {
      ReactRailsUJS.mountComponents(element.parentNode)
    }
  })
}

function handleUnmount() {
  if (ReactRailsUJS) {
    ReactRailsUJS.unmountComponents()
  }
}

function handleStreamRender(event) {
  // Schedule mount after the stream action completes
  // The event.target is the turbo-stream element with the action
  const originalRender = event.detail.render
  event.detail.render = async function (streamElement) {
    await originalRender(streamElement)
    // Mount components after stream action completes
    handleMount()
  }
}

export function setup(ujs) {
  ReactRailsUJS = ujs

  // Turbo Drive navigation events (similar to turbolinks:load)
  document.addEventListener('turbo:load', handleMount)

  // Turbo renders - fires after Turbo renders content
  document.addEventListener('turbo:render', handleMount)

  // Turbo Frame renders
  document.addEventListener('turbo:frame-render', handleMount)

  // Turbo Frame loads (fires after frame content is loaded)
  document.addEventListener('turbo:frame-load', handleMount)

  // Turbo Stream renders - hook into the render process
  document.addEventListener('turbo:before-stream-render', handleStreamRender)

  // Custom event for non-Turbo DOM insertions (e.g., nested forms)
  document.addEventListener('react:mount', handleMount)

  // Cleanup before caching to prevent stale React state
  document.addEventListener('turbo:before-cache', handleUnmount)
}

export function teardown() {
  document.removeEventListener('turbo:load', handleMount)
  document.removeEventListener('turbo:render', handleMount)
  document.removeEventListener('turbo:frame-render', handleMount)
  document.removeEventListener('turbo:frame-load', handleMount)
  document.removeEventListener('turbo:before-stream-render', handleStreamRender)
  document.removeEventListener('react:mount', handleMount)
  document.removeEventListener('turbo:before-cache', handleUnmount)
  ReactRailsUJS = null
}

export default { setup, teardown }
